import type { EntryMode, RegistrationNextAction, RegistrationStatus } from '../types/event';

/** Normalize legacy submitted status to pending_approval. */
export function normalizeRegistrationStatus(status: RegistrationStatus): RegistrationStatus {
  return status === 'submitted' ? 'pending_approval' : status;
}

export function isPaidEntryMode(entryMode?: EntryMode | null): boolean {
  return entryMode === 'open_paid' || entryMode === 'pre_approval_paid';
}

export function isPreApprovalMode(entryMode?: EntryMode | null): boolean {
  return entryMode === 'pre_approval_free' || entryMode === 'pre_approval_paid';
}

export const BUILDER_STATUS_LABELS: Record<RegistrationStatus, string> = {
  pending_approval: 'Waiting for organizer review',
  submitted: 'Waiting for organizer review',
  approved: 'Approved. Complete payment',
  payment_pending: 'Confirming payment',
  confirmed: 'Spot confirmed',
  rejected: 'Not selected',
  withdrawn: 'Withdrawn',
  checked_in: 'Checked in',
  no_show: 'Marked no-show',
  cancelled: 'Cancelled',
  payment_failed: 'Payment failed. Retry',
  refunded: 'Refunded',
};

export const ORGANIZER_STATUS_LABELS: Record<RegistrationStatus, string> = {
  pending_approval: 'Pending review',
  submitted: 'Pending review',
  approved: 'Approved (awaiting pay)',
  payment_pending: 'Awaiting payment',
  confirmed: 'Confirmed',
  rejected: 'Rejected',
  withdrawn: 'Withdrawn',
  checked_in: 'Checked in',
  no_show: 'No-show',
  cancelled: 'Cancelled',
  payment_failed: 'Payment failed',
  refunded: 'Refunded',
};

export const STATUS_BADGE_STYLES: Record<RegistrationStatus, string> = {
  pending_approval: 'bg-amber-950/50 text-amber-300 border border-amber-800',
  submitted: 'bg-amber-950/50 text-amber-300 border border-amber-800',
  approved: 'bg-blue-950/50 text-blue-300 border border-blue-800',
  payment_pending: 'bg-blue-950/50 text-blue-300 border border-blue-800',
  confirmed: 'bg-emerald-950/50 text-emerald-300 border border-emerald-800',
  rejected: 'bg-red-950/50 text-red-300 border border-red-900',
  withdrawn: 'bg-[#111] text-gray-400 border border-white/[0.08]',
  checked_in: 'bg-emerald-950/50 text-emerald-300 border border-emerald-800',
  no_show: 'bg-[#111] text-gray-500 border border-white/[0.08]',
  cancelled: 'bg-[#111] text-gray-400 border border-white/[0.08]',
  payment_failed: 'bg-red-950/50 text-red-300 border border-red-900',
  refunded: 'bg-[#111] text-gray-400 border border-white/[0.08]',
};

export function builderStatusLabel(status: RegistrationStatus, fallback?: string): string {
  const normalized = normalizeRegistrationStatus(status);
  return fallback || BUILDER_STATUS_LABELS[normalized] || BUILDER_STATUS_LABELS[status] || status;
}

export function organizerStatusLabel(status: RegistrationStatus): string {
  const normalized = normalizeRegistrationStatus(status);
  return ORGANIZER_STATUS_LABELS[normalized] || ORGANIZER_STATUS_LABELS[status] || status;
}

export function builderNextAction(
  status: RegistrationStatus,
  entryMode?: EntryMode | null
): RegistrationNextAction {
  const normalized = normalizeRegistrationStatus(status);
  const paid = isPaidEntryMode(entryMode);
  if (normalized === 'pending_approval') return 'wait';
  if ((normalized === 'approved' || normalized === 'payment_failed') && paid) return 'pay';
  if (normalized === 'payment_pending') return 'confirming';
  return 'none';
}

export function builderStatusMessage(
  status: RegistrationStatus,
  entryMode?: EntryMode | null,
  opts?: { publicRejectNote?: string | null; paymentDeadlineAt?: string | null }
): string {
  const normalized = normalizeRegistrationStatus(status);
  const paid = isPaidEntryMode(entryMode);

  switch (normalized) {
    case 'pending_approval':
      return 'Your application is in the review queue. The organizer will notify you when there is a decision.';
    case 'approved':
      return paid
        ? 'You are approved. Complete payment before the deadline to lock in your spot.'
        : 'You are approved. Your spot is confirmed.';
    case 'payment_pending':
      return 'We are confirming your payment. This usually takes a few seconds.';
    case 'confirmed':
      return 'Your spot is confirmed. Check organizer updates below for day-of logistics.';
    case 'rejected':
      return opts?.publicRejectNote?.trim()
        ? opts.publicRejectNote.trim()
        : 'The organizer did not select this entry. You can register a different vehicle if spots remain.';
    case 'withdrawn':
      return 'You withdrew this registration.';
    case 'checked_in':
      return 'You are checked in for this event.';
    case 'no_show':
      return 'This entry was marked as a no-show.';
    case 'cancelled':
      return 'This registration was cancelled.';
    case 'payment_failed':
      return 'Payment did not go through. Try again before the deadline.';
    case 'refunded':
      return 'This entry was refunded.';
    default:
      return 'Registration status updated.';
  }
}

export function entryModeLabel(entryMode?: EntryMode | null): string {
  switch (entryMode) {
    case 'open_free':
      return 'Open registration (free)';
    case 'pre_approval_free':
      return 'Curated registration (free)';
    case 'open_paid':
      return 'Open registration (paid entry)';
    case 'pre_approval_paid':
      return 'Curated registration (paid entry)';
    default:
      return 'Open registration (free)';
  }
}

export function entryModeDescription(entryMode?: EntryMode | null): string {
  switch (entryMode) {
    case 'open_free':
      return 'Builders register immediately. No payment required.';
    case 'pre_approval_free':
      return 'Builders apply first. The organizer approves entries before they are confirmed.';
    case 'open_paid':
      return 'Builders pay at registration. Spot confirms after payment clears.';
    case 'pre_approval_paid':
      return 'Builders apply first. After approval, payment is required to confirm the spot.';
    default:
      return 'Builders register immediately. No payment required.';
  }
}

export function formatPaymentDeadline(deadlineAt: string | null | undefined): string | null {
  if (!deadlineAt) return null;
  const date = new Date(deadlineAt.replace(' ', 'T'));
  if (isNaN(date.getTime())) return null;
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function formatPriceCents(cents: number, currency = 'usd'): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(cents / 100);
}
